/* Processing-state variants - preview-only. Each is a self-contained
   component that renders inside a 16:9 card so they can be compared
   side by side at /promo/processing.

   Naming: PV{letter}_{Slug} keeps them findable and ordered.
*/
import React, { useEffect, useState, useRef } from 'react';
import BrainPulseLoader from '../../components/BrainPulseLoader';
import {
    Sparkles, FileText, Mic, Brain, Youtube,
} from 'lucide-react';

const SURFACE = 'aspect-video relative overflow-hidden rounded-2xl bg-[#1e3a8a]';
const DARK_GRAD = 'absolute inset-0 bg-gradient-to-b from-[#1e3a8a]/55 via-[#1e3a8a]/35 to-[#1e3a8a]/70';

/* ──────────────────────────────────────────────────────────────
   A - Rolling stage caption
   Cycles through narrative stage labels every 2.4s. Same brain
   image (static, no Ken-Burns) so the motion budget goes entirely
   to the caption swap, not the backdrop.
*/
const STAGES_A = [
    'Reading the article…',
    'Listening to the audio…',
    'Distilling the key ideas…',
    'Filing it in your second brain…',
];
export function PVA_StageCaption() {
    const [i, setI] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setI(v => (v + 1) % STAGES_A.length), 2400);
        return () => clearInterval(id);
    }, []);
    return (
        <div className={SURFACE}>
            <img src="/icon-512.png" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-contain p-6 opacity-90" />
            <div className={DARK_GRAD} />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-4">
                <BrainPulseLoader size={48} />
                <div key={i} className="text-sm font-semibold text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.7)] text-center animate-fade-in">
                    {STAGES_A[i]}
                </div>
            </div>
            <style>{`@keyframes fade-in { from { opacity: 0; transform: translateY(4px) } to { opacity: 1; transform: translateY(0) } } .animate-fade-in { animation: fade-in 0.5s ease-out }`}</style>
        </div>
    );
}

/* ──────────────────────────────────────────────────────────────
   B - Live-typing AI summary
   Mimics ChatGPT streaming. White-card overlay on dark backdrop.
*/
const TYPING_TEXT_B = 'This video covers three main ideas. First, consistent sleep timing matters more than total hours…';
export function PVB_TypingSummary() {
    const [chars, setChars] = useState(0);
    useEffect(() => {
        let i = 0;
        const id = setInterval(() => {
            i = i + 1;
            setChars(i);
            if (i > TYPING_TEXT_B.length + 6) i = 0;
        }, 45);
        return () => clearInterval(id);
    }, []);
    return (
        <div className={SURFACE}>
            <img src="/icon-512.png" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-contain p-6 opacity-90" />
            <div className="absolute inset-0 bg-[#1e3a8a]/70" />
            <div className="absolute inset-x-4 top-1/2 -translate-y-1/2 bg-white/95 backdrop-blur rounded-xl p-3 shadow-lg">
                <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700 mb-1.5">
                    <Sparkles className="w-3 h-3" />
                    AI is drafting your summary
                </div>
                <p className="text-xs text-gray-700 leading-relaxed">
                    {TYPING_TEXT_B.slice(0, Math.min(chars, TYPING_TEXT_B.length))}
                    <span className="inline-block w-[1px] h-[1em] bg-emerald-600 ml-0.5 align-text-bottom animate-pulse" />
                </p>
            </div>
        </div>
    );
}

/* ──────────────────────────────────────────────────────────────
   C - Source-aware ambient backdrop
   Demo cycles through 3 source types (YouTube → TikTok → Article)
   every 3s so reviewers see the variety. Each frame uses a brand
   color + matching icon.
*/
const SOURCES_C = [
    { kind: 'YouTube', bg: 'from-red-600 via-red-700 to-red-900', Icon: Youtube, label: 'Watching YouTube…' },
    { kind: 'TikTok', bg: 'from-zinc-800 via-zinc-900 to-black', Icon: Mic, label: 'Transcribing TikTok…' },
    { kind: 'Article', bg: 'from-amber-100 via-amber-50 to-white', Icon: FileText, label: 'Reading article…', light: true },
];
export function PVC_SourceAware() {
    const [i, setI] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setI(v => (v + 1) % SOURCES_C.length), 3000);
        return () => clearInterval(id);
    }, []);
    const s = SOURCES_C[i];
    return (
        <div className={`${SURFACE} bg-gradient-to-br ${s.bg}`} key={i}>
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2.5">
                <s.Icon className={`w-12 h-12 ${s.light ? 'text-amber-700' : 'text-white'} drop-shadow-lg`} />
                <div className={`text-sm font-semibold ${s.light ? 'text-amber-900' : 'text-white'} drop-shadow-md`}>{s.label}</div>
            </div>
            <div className="absolute inset-x-0 bottom-0 h-1 bg-white/30 overflow-hidden">
                <div className="h-full bg-white animate-c-progress" />
            </div>
            <style>{`@keyframes c-prog { from { width: 0 } to { width: 100% } } .animate-c-progress { animation: c-prog 3s linear infinite }`}</style>
        </div>
    );
}

/* ──────────────────────────────────────────────────────────────
   D - Particle synapse field
   Pure canvas. Nodes drift, lines re-wire on proximity.
   Tech-y, abstract, brand-themed without leaning on photos.
*/
export function PVD_Particles() {
    const ref = useRef(null);
    useEffect(() => {
        const canvas = ref.current;
        if (!canvas) return undefined;
        const ctx = canvas.getContext('2d');
        let raf = 0;
        let stopped = false;
        const resize = () => {
            const r = canvas.getBoundingClientRect();
            canvas.width = r.width * window.devicePixelRatio;
            canvas.height = r.height * window.devicePixelRatio;
            ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
        };
        resize();
        window.addEventListener('resize', resize);
        const nodes = Array.from({ length: 24 }, () => ({
            x: Math.random() * canvas.width / window.devicePixelRatio,
            y: Math.random() * canvas.height / window.devicePixelRatio,
            vx: (Math.random() - 0.5) * 0.4,
            vy: (Math.random() - 0.5) * 0.4,
        }));
        const tick = () => {
            if (stopped) return;
            const w = canvas.width / window.devicePixelRatio;
            const h = canvas.height / window.devicePixelRatio;
            ctx.clearRect(0, 0, w, h);
            // Background
            ctx.fillStyle = '#0f172a';
            ctx.fillRect(0, 0, w, h);
            for (const n of nodes) {
                n.x += n.vx;
                n.y += n.vy;
                if (n.x < 0 || n.x > w) n.vx *= -1;
                if (n.y < 0 || n.y > h) n.vy *= -1;
            }
            // Lines between near nodes
            for (let i = 0; i < nodes.length; i++) {
                for (let j = i + 1; j < nodes.length; j++) {
                    const dx = nodes[i].x - nodes[j].x;
                    const dy = nodes[i].y - nodes[j].y;
                    const d = Math.hypot(dx, dy);
                    if (d < 90) {
                        ctx.strokeStyle = `rgba(16, 185, 129, ${(1 - d / 90) * 0.6})`;
                        ctx.lineWidth = 1;
                        ctx.beginPath();
                        ctx.moveTo(nodes[i].x, nodes[i].y);
                        ctx.lineTo(nodes[j].x, nodes[j].y);
                        ctx.stroke();
                    }
                }
            }
            // Nodes
            for (const n of nodes) {
                ctx.fillStyle = '#34d399';
                ctx.beginPath();
                ctx.arc(n.x, n.y, 1.8, 0, Math.PI * 2);
                ctx.fill();
            }
            raf = requestAnimationFrame(tick);
        };
        tick();
        return () => { stopped = true; cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
    }, []);
    return (
        <div className={SURFACE}>
            <canvas ref={ref} className="absolute inset-0 w-full h-full" />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                <Brain className="w-9 h-9 text-emerald-300 drop-shadow-[0_0_8px_rgba(52,211,153,0.6)]" />
                <div className="text-xs font-semibold text-emerald-100">Wiring it together…</div>
            </div>
        </div>
    );
}

/* ──────────────────────────────────────────────────────────────
   E - Sonar ripple on static brain
   Calmest variant. Brain dead-center, three concentric emerald
   ripples emanate outward every 3s.
*/
export function PVE_SonarRipple() {
    return (
        <div className={SURFACE}>
            <img src="/icon-512.png" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-contain p-6 opacity-90" />
            <div className={DARK_GRAD} />
            <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative w-20 h-20">
                    {[0, 1, 2].map(i => (
                        <div
                            key={i}
                            className="absolute inset-0 rounded-full border-2 border-emerald-300/80 animate-e-ripple"
                            style={{ animationDelay: `${i * 1}s` }}
                        />
                    ))}
                    <Brain className="absolute inset-0 m-auto w-9 h-9 text-emerald-200 drop-shadow-[0_0_10px_rgba(52,211,153,0.7)]" />
                </div>
            </div>
            <div className="absolute inset-x-0 bottom-3 text-center text-xs font-semibold text-emerald-100 drop-shadow">Thinking…</div>
            <style>{`@keyframes e-ripple { 0% { transform: scale(0.6); opacity: 0.8 } 100% { transform: scale(2.8); opacity: 0 } } .animate-e-ripple { animation: e-ripple 3s ease-out infinite; transform-origin: center; }`}</style>
        </div>
    );
}

/* ──────────────────────────────────────────────────────────────
   F - Skeleton fade-in of eventual card
   Most polished. Ghost of the eventual bookmark card with each
   slot fading from skeleton → real value as stages complete.
   Demo cycles through 4 stages on a 1.4s beat.
*/
const F_STAGES = [
    { thumb: false, title: false, snippet: false, tags: false, badge: 'Reviewing…' },
    { thumb: true,  title: false, snippet: false, tags: false, badge: 'Transcribing…' },
    { thumb: true,  title: true,  snippet: false, tags: false, badge: 'AI working' },
    { thumb: true,  title: true,  snippet: true,  tags: true,  badge: 'Done ✓' },
];
export function PVF_Skeleton() {
    const [i, setI] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setI(v => (v + 1) % F_STAGES.length), 1400);
        return () => clearInterval(id);
    }, []);
    const s = F_STAGES[i];
    return (
        <div className={SURFACE + ' bg-zinc-100'}>
            <div className="absolute inset-0 p-3 flex flex-col gap-2">
                {/* Thumbnail */}
                <div className="relative h-20 rounded-lg overflow-hidden bg-zinc-200">
                    {s.thumb ? (
                        <div className="w-full h-full" style={{ backgroundImage: 'url(/play-store/pasta.png)', backgroundSize: 'cover', backgroundPosition: 'center' }} />
                    ) : (
                        <div className="absolute inset-0 bg-gradient-to-r from-zinc-200 via-zinc-300 to-zinc-200 animate-f-shimmer" />
                    )}
                    <span className="absolute top-1.5 left-1.5 bg-emerald-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">{s.badge}</span>
                </div>
                {/* Title */}
                {s.title ? (
                    <div className="text-[12px] font-bold text-zinc-800 leading-tight">15-Minute Garlic Butter Pasta</div>
                ) : (
                    <div className="h-3 rounded bg-gradient-to-r from-zinc-200 via-zinc-300 to-zinc-200 animate-f-shimmer" style={{ width: '80%' }} />
                )}
                {/* Snippet */}
                {s.snippet ? (
                    <div className="text-[10px] text-zinc-500 leading-snug">…boil pasta, sauté garlic, toss with cheese…</div>
                ) : (
                    <>
                        <div className="h-2 rounded bg-gradient-to-r from-zinc-200 via-zinc-300 to-zinc-200 animate-f-shimmer" style={{ width: '95%' }} />
                        <div className="h-2 rounded bg-gradient-to-r from-zinc-200 via-zinc-300 to-zinc-200 animate-f-shimmer" style={{ width: '70%' }} />
                    </>
                )}
                {/* Tags */}
                <div className="flex gap-1 mt-auto">
                    {s.tags ? (
                        <>
                            <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-orange-50 text-orange-700">recipe</span>
                            <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600">pasta</span>
                        </>
                    ) : (
                        <>
                            <div className="h-3 rounded w-10 bg-gradient-to-r from-zinc-200 via-zinc-300 to-zinc-200 animate-f-shimmer" />
                            <div className="h-3 rounded w-8 bg-gradient-to-r from-zinc-200 via-zinc-300 to-zinc-200 animate-f-shimmer" />
                        </>
                    )}
                </div>
            </div>
            <style>{`@keyframes f-shimmer { 0% { background-position: -200% 0 } 100% { background-position: 200% 0 } } .animate-f-shimmer { background-size: 200% 100%; animation: f-shimmer 1.5s linear infinite }`}</style>
        </div>
    );
}

export const PROCESSING_VARIANTS = [
    { key: 'A', title: 'A - Rolling stage caption', subtitle: 'Same brain image, rotating narrative captions every 2.4s.', Component: PVA_StageCaption, vibe: 'narrative, trust-building' },
    { key: 'B', title: 'B - Live-typing AI summary', subtitle: 'Mimics ChatGPT streaming. Highest "the AI is actually working" signal.', Component: PVB_TypingSummary, vibe: 'magic, premium' },
    { key: 'C', title: 'C - Source-aware ambient', subtitle: 'Per-platform color + icon. Demo cycles YouTube → TikTok → Article.', Component: PVC_SourceAware, vibe: 'context-aware, delightful' },
    { key: 'D', title: 'D - Particle synapse field', subtitle: 'Canvas-rendered constellation of dots wiring together.', Component: PVD_Particles, vibe: 'tech-y, abstract, modern' },
    { key: 'E', title: 'E - Sonar ripples (calm)', subtitle: 'Static brain, three concentric emerald ripples emanating.', Component: PVE_SonarRipple, vibe: 'calm, less distracting' },
    { key: 'F', title: 'F - Skeleton fade-in', subtitle: 'The eventual bookmark card assembles itself in real time.', Component: PVF_Skeleton, vibe: 'polished, SaaS-grade' },
];

export default PROCESSING_VARIANTS;
